import Dexie from 'dexie';
// Leaf modules only, never `./persistenceV2/lifecycle` or `./persistenceV2/writeFence`:
// this file is on the mic, connectivity and chunk paths, and a value import from
// either would drag a real `dbV2` or the fence's channel/uuid/logger into them.
import { isMemoryOnlyWriteError } from './persistenceV2/memoryOnlyWriteError';
import { PersistenceUnavailableError } from './persistenceV2/persistenceUnavailableError';
import { TooLowStorageError } from './types';

/**
 * True when a getUserMedia() rejection is an acquisition failure, not a
 * permission denial. The cause may be contention, hardware, OS or browser, so
 * user-facing copy must stay non-definitive.
 */
export const isDeviceInUseDOMException = (error: unknown): error is DOMException =>
  error instanceof DOMException &&
  (error.name === 'NotReadableError' || error.name === 'AbortError');

const SESSION_URL_PATTERNS = [
  /\/?sessions\/[^/]+/, // ml-scribe: /sessions/<id>/...
  /\/[0-9a-f-]{20,}\/(transcribe-chunk|transcript)/, // t11n-v2: /<session_id>/transcribe-chunk or /<session_id>/transcript (UUID-like segment only)
];

// Structural shape of an axios-like HTTP error, declared here so the kit needs
// no HTTP dependency. Axios-typed helpers stay app-side.
interface HttpErrorLike {
  isAxiosError?: boolean;
  code?: string;
  response?: { status?: number } | null;
  config?: { url?: string } | null;
}

/** Returns true when the error is a 404 from a session endpoint, indicating the session no longer exists. */
export const isSessionNotFoundError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as HttpErrorLike;

  if (candidate.isAxiosError !== true) return false;

  if (candidate.response?.status !== 404) return false;

  const url = candidate.config?.url ?? '';

  return SESSION_URL_PATTERNS.some((pattern) => pattern.test(url));
};

/**
 * PHI-safe error projection for Datadog-forwarded logs: the transport serialises
 * whatever it is handed, so a raw axios error would export its request url/body,
 * and `message` can embed the same — only name/status/code ship.
 */
export const getSafeErrorFields = (
  error: unknown
): { errorName: string; status?: number; code?: string } => {
  const errorName = error instanceof Error ? error.name : typeof error;

  if (typeof error === 'object' && error !== null) {
    const candidate = error as HttpErrorLike;
    if (candidate.isAxiosError === true) {
      return { errorName, status: candidate.response?.status, code: candidate.code };
    }
  }

  return { errorName };
};

/**
 * The TOLERANCE test only — whether a failure may be absorbed rather than
 * refused. Arming memory-only is a separate, narrower decision at every site
 * (`isStoreUnusableForConsult` / `isStoreHealthFailure`): refusing a clinician a
 * recording over a one-off glitch is the worse outcome, latching a whole consult
 * to memory on one is not.
 *
 * An allowlist, not "everything that is not a stale fence": a TypeError from a
 * refactor is a fault in the write, and absorbing it as storage degradation
 * would run the recording on a bug instead of reporting it.
 */
export const isStorageDegradation = (err: unknown): boolean =>
  isMemoryOnlyWriteError(err) ||
  err instanceof TooLowStorageError ||
  err instanceof Dexie.DexieError ||
  // Not a DexieError: the bounded open window expiring is its own class.
  err instanceof PersistenceUnavailableError ||
  // Browser-native quota exhaustion arrives as a DOMException.
  (err instanceof Error && err.name === 'QuotaExceededError');

// Read structurally: `DexieError.inner` is typed `any`, and the wrapped value
// is whatever IDB threw rather than a guaranteed Error.
const wrappedErrorName = (err: unknown): string | undefined => {
  if (!(err instanceof Dexie.DexieError)) return undefined;
  const { inner } = err as unknown as { inner?: unknown };
  if (typeof inner !== 'object' || inner === null) return undefined;
  const { name } = inner as { name?: unknown };
  return typeof name === 'string' && name.length > 0 ? name : undefined;
};

/**
 * Dexie names the exception it raised, not the one it caught, so an exhausted
 * quota reports as `AbortError` with the real cause hidden in `inner`.
 */
export const getUnwrappedErrorName = (err: unknown): string =>
  wrappedErrorName(err) ?? getSafeErrorFields(err).errorName;

// The store itself is broken, as opposed to this one write failing.
const STORE_HEALTH_ERROR_NAMES = new Set([
  'QuotaExceededError',
  'UnknownError',
  'DatabaseClosedError',
  // A store that will not open is dead for the rest of the consult: Dexie caches
  // the open failure and re-raises it on every later transaction, and `withReopen`
  // has already spent its reopen by the time one of these escapes.
  'OpenFailedError',
  // Same condition on the timeout side; the `instanceof` below is belt-and-braces
  // for it, because a prototype check does not survive a realm boundary.
  'PersistenceUnavailableError',
]);

/**
 * Narrower than `isStorageDegradation` on purpose: tolerance is broad, arming is
 * narrow, everywhere. A failed write can be tolerated and retried, but arming
 * memory-only latches a whole consult to memory, so a transient transaction
 * fault — or a fault in the write itself — must never flip it.
 *
 * Either name can carry the evidence, because Dexie wraps in both directions: a
 * spent quota is `AbortError{inner: QuotaExceededError}`, a dead store is
 * `DatabaseClosedError{inner: OpenFailedError}`. Matching one alone misses one.
 */
// A DataError usually means we handed IndexedDB a bad value (our bug — tolerate
// and report, never arm). But the disk failing to write blobs surfaces under the
// same name ("Failed to write blobs (IOError)"), and that one is a dead store.
// The IOError marker is the only signal that separates them — "failed to write"
// alone also matches a malformed value ("Failed to write index key").
const dexieInner = (err: unknown): unknown =>
  err instanceof Dexie.DexieError
    ? (err as unknown as { inner?: unknown }).inner
    : undefined;

// Dexie names the wrapped error but hides its message on `inner`; read both, or a
// wrapped IO failure is checked against the wrapper's message and never arms.
const errorMessageChain = (err: unknown): string => {
  const outer = err instanceof Error ? err.message : '';
  const inner = dexieInner(err);
  const innerMessage =
    typeof inner === 'object' && inner !== null
      ? (inner as { message?: unknown }).message
      : undefined;
  return `${outer} ${typeof innerMessage === 'string' ? innerMessage : ''}`;
};

const isBlobWriteIoFailure = (err: unknown): boolean =>
  getUnwrappedErrorName(err) === 'DataError' && /ioerror/i.test(errorMessageChain(err));

export const isStoreHealthFailure = (err: unknown): boolean => {
  if (err instanceof PersistenceUnavailableError) return true;
  return (
    STORE_HEALTH_ERROR_NAMES.has(getSafeErrorFields(err).errorName) ||
    STORE_HEALTH_ERROR_NAMES.has(getUnwrappedErrorName(err)) ||
    isBlobWriteIoFailure(err)
  );
};

/**
 * The start's arming test: a broken store, or a quota floor already breached.
 * Both mean the store cannot take this consult's audio, and neither heals within
 * it.
 *
 * `TooLowStorageError` stays out of `STORE_HEALTH_ERROR_NAMES` because the store
 * never raises it — it is our own pre-flight verdict on
 * `navigator.storage.estimate()`, which only the start runs. Adding it there
 * would mix policy verdicts into a list of store-raised evidence and widen the
 * mid-recording arm for a name no write can produce.
 */
export const isStoreUnusableForConsult = (err: unknown): boolean =>
  isStoreHealthFailure(err) || err instanceof TooLowStorageError;
